import React, { useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import { useAuth } from '../contexts/AuthContext'
import { useWallet } from '../contexts/WalletContext'
import { useUserInfo } from '../contexts/UserInfoContext'
import { USER_SUMMARY_UPDATED_EVENT } from '../services/pointsService'
import { hasActiveLiveTv } from '../lib/liveTv'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface DashboardPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const DashboardPage: React.FC<DashboardPageProps> = ({ themeConfig }) => {
  // Navigation hook
  const navigate = useNavigate()
  
  // Auth context
  const { user } = useAuth()
  const { walletData, isLoadingWallet, formatCurrency } = useWallet()
  const { userInfo, getPrimaryBankAccount, refreshUserInfo } = useUserInfo()
  const serviceConfigs = Array.isArray(userInfo?.serviceConfigs) ? userInfo.serviceConfigs : []
  const isServiceEnabled = (serviceType: 'lottery' | 'casino') => {
    const target = serviceConfigs.find((item: any) => item?.serviceType?.toLowerCase?.() === serviceType)
    return target ? target.isActive !== false : true
  }
  // สิทธิ์ระดับ user (admin ปิดเฉพาะบัญชี)
  const userLotteryEnabled = isServiceEnabled('lottery')
  const userCasinoEnabled = isServiceEnabled('casino')
  // ปิดทั้งระบบระดับ tenant (ปิดปรับปรุงระบบ)
  const tenantLotteryEnabled = themeConfig?.settings?.lotteryEnabled !== false
  const tenantCasinoEnabled = themeConfig?.settings?.casinoEnabled !== false
  const showLiveTv = hasActiveLiveTv(
    themeConfig?.settings?.liveTvChannels || [],
    themeConfig?.settings?.liveTvEnabled,
    themeConfig?.settings?.huayYoutubeLive
  )

  // Swal "ปิดปรับปรุงระบบ" เมื่อ tenant ปิดระบบหวย
  const showLotteryMaintenance = () => {
    void Swal.fire({
      icon: 'warning',
      title: 'ปิดปรับปรุงระบบ',
      text: 'ขณะนี้ระบบหวยปิดปรับปรุงชั่วคราว กรุณาลองใหม่อีกครั้งภายหลัง',
      confirmButtonText: 'ตกลง',
      confirmButtonColor: '#ffc107'
    })
  }

  // ปุ่มหวย: tenant ปิด → คลิกแล้วเด้ง Swal ปิดปรับปรุง / user ถูกปิดสิทธิ์ → ปุ่ม disabled / ปกติ → navigate
  const renderLotteryButton = (opts: { className: string; icon: string; label: string; path: string }) => {
    if (!tenantLotteryEnabled) {
      return (
        <a
          href="#"
          className={opts.className}
          onClick={(e) => {
            e.preventDefault()
            showLotteryMaintenance()
          }}
        >
          <i className={`${opts.icon} fs-28`}></i>
          <p>{opts.label}</p>
        </a>
      )
    }
    if (!userLotteryEnabled) {
      return (
        <button
          type="button"
          className={`${opts.className} w-100 opacity-60`}
          disabled
          title="ปิดการใช้งานหวยโดยผู้ดูแลระบบ"
        >
          <i className="fas fa-ban fs-28"></i>
          <p>ปิด{opts.label}</p>
        </button>
      )
    }
    return (
      <a
        href="#"
        className={opts.className}
        onClick={(e) => {
          e.preventDefault()
          navigate(opts.path)
        }}
      >
        <i className={`${opts.icon} fs-28`}></i>
        <p>{opts.label}</p>
      </a>
    )
  }
  const pointsValue = useMemo(() => {
    const loyaltyPoints = Number(userInfo?.loyaltyPoints?.currentPoints)
    if (Number.isFinite(loyaltyPoints)) return loyaltyPoints
    const bonusPoints = Number((Array.isArray(userInfo?.bonuses) ? userInfo?.bonuses?.[0]?.points : 0) || 0)
    return Number.isFinite(bonusPoints) ? bonusPoints : 0
  }, [userInfo])
  const pointsText = `${pointsValue.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pts`

  useEffect(() => {
    const handleUserSummaryUpdated = () => {
      void refreshUserInfo()
    }

    window.addEventListener(USER_SUMMARY_UPDATED_EVENT, handleUserSummaryUpdated)
    return () => window.removeEventListener(USER_SUMMARY_UPDATED_EVENT, handleUserSummaryUpdated)
  }, [refreshUserInfo])

  // User data from authentication, wallet, and user info
  const userData = {
    username: userInfo?.username || user?.username || 'ผู้ใช้',
    fullName: userInfo?.name || `${(user as any)?.firstName || ''} ${(user as any)?.lastName || ''}`.trim() || user?.username || 'ผู้ใช้',
    balance: walletData ? formatCurrency(walletData.balance.main) : (isLoadingWallet ? 'กำลังโหลด...' : '0.00'),
    status: userInfo?.isActiveAccount !== false ? 'ปกติ' : 'ไม่ปกติ',
    bankAccount: getPrimaryBankAccount(),
    mobile: userInfo?.mobile || user?.mobile || '',
    email: userInfo?.email || (user as any)?.email || '',
    isAutoDeposit: userInfo?.isAutoDeposit || false,
    isAutoWithdraw: userInfo?.isAutoWithdraw || false
  }

  return (
    <div className="content pb-3">
      <div className="container">
        {/* User Balance Card */}
        <div className="wrap wrap-login bdr-5 bx-sd">
          <div className="bgi-2 wrap w-100 justify-content-center bdr-5">
            <div className="bgc-14 w-100 position-relative">
              <a
                href="#"
                className="t-regular txt-link-m p-2 wrap wrap-c"
                onClick={(e) => e.preventDefault()}
              >
                <i className="fas fa-chart-bar me-1"></i>
                <p className="fs-14">เงินคงเหลือ</p>
              </a>

              <div className="text-center w-100 p-2">
                <small className={`bdr-10 bgg-2 t-medium d-inline px-2 ${userData.status === 'ปกติ' ? 'c-green' : 'c-red'}`}>
                  สถานะ : {userData.status}
                </small>
                <h1 className="c-main t-medium mt-2 mb-1">{userData.balance}</h1>
                <h6 className="c-white fs-14 mb-4">{userData.username} ({userData.fullName})</h6>
              </div>
            </div>
          </div>
        </div>

        <div className="bdr-5 bx-sd bgc-7 p-2 mt-2 mb-2">
          <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-2">
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  width: '56px',
                  height: '56px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.18)'
                }}
              >
                <i className="fas fa-coins c-white fs-24"></i>
              </div>
              <div className="ms-3 min-w-0">
                <p className="c-dark mb-1 fs-14">คะแนนของคุณ</p>
                <h4 className="c-red t-medium mb-1">{pointsText}</h4>
                <small className="c-dark opacity-75">สะสมแต้มไว้แลกเครดิตเพิ่มเติมได้ในหน้าคะแนน</small>
              </div>
            </div>

            <Link
              to="/points/moneytopoint"
              className="check-button bgg-5 c-white text-center text-decoration-none flex-shrink-0"
              style={{ minWidth: '170px' }}
            >
              <i className="fas fa-external-link-alt me-2"></i>
              ดูคะแนนทั้งหมด
            </Link>
          </div>
        </div>

        {/* Main Action Buttons */}
        <div className="row mx--1 py-2">
          <div className="col-6 px-1 mb-2">
            {renderLotteryButton({
              className: 'check-button bgg-5 bdc-2 c-white bx-sd d-block',
              icon: 'fas fa-hand-pointer',
              label: 'แทงหวย',
              path: '/lotto'
            })}
          </div>
          <div className="col-6 px-1 mb-2">
            {tenantCasinoEnabled && userCasinoEnabled ? (
              <a
                href="#"
                className="check-button bgg-5 bdc-2 c-white bx-sd d-block"
                onClick={(e) => {
                  e.preventDefault()
                  navigate('/casino')
                }}
              >
                <i className="fas fa-chess-rook fs-28"></i>
                <p>เกมส์ กีฬา คาสิโน</p>
              </a>
            ) : (
              <button
                type="button"
                className="check-button bgg-5 bdc-2 c-white bx-sd d-block w-100 opacity-60"
                disabled
                title={!tenantCasinoEnabled ? 'ปิดปรับปรุงระบบเกมส์' : 'ปิดการใช้งานเกมส์โดยผู้ดูแลระบบ'}
              >
                <i className="fas fa-ban fs-28"></i>
                <p>{!tenantCasinoEnabled ? 'ปิดปรับปรุงระบบ' : 'ปิดเข้าเล่นเกมส์'}</p>
              </button>
            )}
          </div>

          {/* Lottery Types */}
          <div className="col-3 px-1 mb-2">
            {renderLotteryButton({
              className: 'check-button bgc-4 c-dark bx-sd d-block',
              icon: 'fas fa-hand-pointer',
              label: 'แทงหวย',
              path: '/lotto'
            })}
          </div>
          <div className="col-3 px-1 mb-2">
            {renderLotteryButton({
              className: 'check-button bgc-4 c-dark bx-sd d-block',
              icon: 'fas fa-bullseye',
              label: 'หวยยี่กี',
              path: '/yeekee'
            })}
          </div>
          <div className="col-3 px-1 mb-2">
            {renderLotteryButton({
              className: 'check-button bgc-4 c-dark bx-sd d-block',
              icon: 'fas fa-list-ol',
              label: 'หวยชุด',
              path: '/set'
            })}
          </div>
          <div className="col-3 px-1 mb-2">
            <a
              href="#"
              className="check-button bgc-4 c-dark bx-sd d-block"
              onClick={(e) => {
                e.preventDefault()
                navigate('/lottery-result')
              }}
            >
              <i className="fas fa-trophy fs-28"></i>
              <p>ผลรางวัล</p>
            </a>
          </div>

          {/* Utilities */}
          <div className="col-3 px-1 mb-2">
            <a
              href="#"
              className="check-button bgc-4 c-dark bx-sd d-block"
              onClick={(e) => {
                e.preventDefault()
                navigate('/ticket')
              }}
            >
              <i className="fas fa-file-invoice fs-28"></i>
              <p>โพยหวย</p>
            </a>
          </div>
          <div className="col-3 px-1 mb-2">
            <a
              href="#"
              className="check-button bgc-4 c-dark bx-sd d-block"
              onClick={(e) => {
                e.preventDefault()
                navigate('/number-set')
              }}
            >
              <i className="fas fa-sort-numeric-up-alt fs-28"></i>
              <p>สร้างเลขชุด</p>
            </a>
          </div>
          <div className="col-3 px-1 mb-2">
            <a
              href="#"
              className="check-button bgc-4 c-dark bx-sd d-block"
              onClick={(e) => {
                e.preventDefault()
                navigate('/affiliate')
              }}
            >
              <i className="fas fa-users fs-28"></i>
              <p>แนะนำเพื่อน</p>
            </a>
          </div>
          <div className="col-3 px-1 mb-2">
            <a
              href="#"
              className="check-button bgc-4 c-dark bx-sd d-block"
              onClick={(e) => {
                e.preventDefault()
                navigate('/contact')
              }}
            >
              <i className="fas fa-envelope fs-28"></i>
              <p>จดหมาย</p>
            </a>
          </div>
          <div className="col-3 px-1 mb-2">
            <a
              href="#"
              className="check-button bgc-4 c-dark bx-sd d-block"
              onClick={(e) => {
                e.preventDefault()
                navigate('/cashback')
              }}
            >
              <i className="fas fa-money-bill-wave fs-28"></i>
              <p>รับเครดิตเงินคืน</p>
            </a>
          </div>
          <div className="col-3 px-1 mb-2">
            <a
              href="#"
              className="check-button bgc-4 c-dark bx-sd d-block"
              onClick={(e) => {
                e.preventDefault()
                navigate('/promotion')
              }}
            >
              <i className="fas fa-gift fs-28"></i>
              <p>โบนัส</p>
            </a>
          </div>
          <div className="col-3 px-1 mb-2">
            {showLiveTv ? (
              <a
                href="#"
                className="check-button bgc-4 c-dark bx-sd d-block"
                onClick={(e) => {
                  e.preventDefault()
                  navigate('/live-tv')
                }}
              >
                <i className="fas fa-tv fs-28" />
                <p>ดูถ่ายทอดสด</p>
              </a>
            ) : (
              <button
                type="button"
                className="check-button bgc-4 c-dark bx-sd d-block w-100 opacity-60"
                disabled
                aria-label="placeholder shortcut"
              >
                <span className="d-block" style={{ height: '32px' }}></span>
                <p className="mb-0">&nbsp;</p>
              </button>
            )}
          </div>
          <div className="col-3 px-1 mb-2">
            <button
              type="button"
              className="check-button bgc-4 c-dark bx-sd d-block w-100 opacity-60"
              disabled
              aria-label="placeholder shortcut"
            >
              <span className="d-block" style={{ height: '32px' }}></span>
              <p className="mb-0">&nbsp;</p>
            </button>
          </div>

          {/* Financial Operations */}
          <div className="col-6 px-1 mb-2">
            <a
              href="#"
              className="check-button bgg-4 c-white bx-sd"
              onClick={(e) => {
                e.preventDefault()
                navigate('/topup')
              }}
            >
              <i className="fas fa-credit-card me-2"></i>เติมเงิน
            </a>
          </div>
          <div className="col-6 px-1 mb-2">
            <a
              href="#"
              className="check-button bgg-1 c-white bx-sd"
              onClick={(e) => {
                e.preventDefault()
                navigate('/withdrawal')
              }}
            >
              <i className="fas fa-dollar-sign me-2"></i>ถอนเงิน
            </a>
          </div>
          <div className="col-6 px-1 mb-2">
            <a
              href="#"
              className="check-button bgc-4 c-dark bx-sd"
              onClick={(e) => {
                e.preventDefault()
                navigate('/transaction')
              }}
            >
              <i className="fas fa-exchange-alt me-2"></i>รายการฝาก-ถอน
            </a>
          </div>
          <div className="col-6 px-1 mb-2">
            <a
              href="#"
              className="check-button bgc-4 c-dark bx-sd"
              onClick={(e) => {
                e.preventDefault()
                navigate('/financial-report')
              }}
            >
              <i className="fas fa-chart-bar me-2"></i>รายการการเงิน
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
